import { api } from '@/lib/api';
import type { ChatProject } from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 제공되면 수정 모드, 없으면 생성 모드. */
  project?: ChatProject;
}

/**
 * 프로젝트 생성 / 이름·설명 수정 모달.
 * Esc / 백드롭 클릭 = 닫기. Enter = submit.
 */
export function ChatProjectFormDialog({ open, onOpenChange, project }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(project?.name ?? '');
    setDescription(project?.description ?? '');
    setError(null);
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, project]);

  const createMutation = useMutation({
    mutationFn: () =>
      api.chatProjectCreate({
        name: name.trim(),
        description: description.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'projects'] });
      onOpenChange(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : '생성 실패'),
  });

  const renameMutation = useMutation({
    mutationFn: () => {
      if (!project) throw new Error('수정 모드가 아닙니다.');
      return api.chatProjectRename(project.id, {
        name: name.trim(),
        description: description.trim() === '' ? null : description.trim(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'projects'] });
      onOpenChange(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : '수정 실패'),
  });

  if (!open) return null;

  const isEdit = !!project;
  const submitting = createMutation.isPending || renameMutation.isPending;

  const submit = () => {
    if (!name.trim()) {
      setError('이름을 입력하세요.');
      return;
    }
    setError(null);
    if (isEdit) renameMutation.mutate();
    else createMutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: ChatSearchDialog 와 동일 패턴 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? '프로젝트 수정' : '새 프로젝트'}
        className="w-full max-w-md rounded-2xl border border-border bg-surface shadow-[var(--shadow-card)] overflow-hidden"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onOpenChange(false);
          } else if (
            e.key === 'Enter' &&
            !e.shiftKey &&
            (e.target as HTMLElement).tagName !== 'TEXTAREA'
          ) {
            e.preventDefault();
            submit();
          }
        }}
      >
        <div className="px-5 pt-5 pb-1 text-sm font-semibold">
          {isEdit ? '프로젝트 수정' : '새 프로젝트'}
        </div>

        <div className="px-5 py-4 space-y-3">
          <label className="block">
            <span className="block text-xs text-foreground-muted mb-1">이름</span>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="프로젝트 이름"
              className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm outline-none focus:border-accent"
            />
          </label>
          <label className="block">
            <span className="block text-xs text-foreground-muted mb-1">설명 (선택)</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="프로젝트에 대한 짧은 설명"
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm outline-none focus:border-accent resize-none"
            />
          </label>
          {error ? <div className="text-xs text-destructive">{error}</div> : null}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-8 px-3 rounded-md text-sm text-foreground-muted hover:bg-surface-muted"
          >
            취소
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !name.trim()}
            className="h-8 px-3 rounded-md text-sm bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-40"
          >
            {isEdit ? '저장' : '만들기'}
          </button>
        </div>
      </div>
    </div>
  );
}
